import { useState, useRef } from 'react';
import { useAuth } from '../lib/auth-context';
import { postIntelligenceSimilarity, type SimilarityResult, type SimilarityResponse } from '../lib/api';
import { Link } from 'react-router-dom';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { apiErrorFromResponse } from '../lib/api-errors';

const MAX_CHARS = 4000;

interface CoverageGap {
  readonly code: string;
  readonly message: string;
}

function confidenceBadge(confidence: string): React.ReactElement {
  const colors: Record<string, string> = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[confidence] ?? 'bg-slate-100 text-slate-600'}`}>
      {confidence}
    </span>
  );
}

export function IntelligenceSimilarityPage(): React.ReactElement {
  const { session } = useAuth();

  const [queryText, setQueryText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SimilarityResponse | null>(null);
  const [requestError, setRequestError] = useState<Error | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charCount = queryText.length;
  const overLimit = charCount > MAX_CHARS;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!session || overLimit || queryText.trim().length === 0) return;

    setLoading(true);
    setRequestError(null);
    setResult(null);

    try {
      const res = await postIntelligenceSimilarity(session.access_token, {
        query_text: queryText,
        limit: 10,
      });
      const body = (await res.json().catch(() => null)) as SimilarityResponse | { error: string } | null;
      if (!res.ok) {
        setRequestError(apiErrorFromResponse(res.status, body, 'Similarity search failed.'));
      } else {
        setResult(body as SimilarityResponse);
      }
    } catch {
      setRequestError(new Error('Similarity search failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  }

  const coverageGaps: readonly CoverageGap[] = result?.coverage_gaps ?? [];
  const results: readonly SimilarityResult[] = result?.results ?? [];
  const corpusIncomplete = coverageGaps.some((g) => g.code === 'EMBEDDING_CORPUS_INCOMPLETE');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Intelligence: Similarity Workbench</h2>
        <Link
          to="/intelligence/documents"
          className="text-sm font-medium text-sentinel-700 hover:text-sentinel-900"
        >
          ← Documents
        </Link>
      </div>

      {/* Privacy note — required by CCP-6 and E2E-RIL-004 */}
      <div
        className="rounded-md bg-blue-50 border border-blue-200 p-4 mb-6 text-sm text-blue-800"
        data-testid="privacy-note"
      >
        <span className="font-semibold">Privacy note:</span> Text entered below is{' '}
        <span className="font-medium">not stored</span>. It is processed transiently to find
        similar regulatory findings and is never persisted to any database, log, or cache.
      </div>

      <form onSubmit={handleSubmit} className="mb-8">
        <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="similarity-query">
          Paste a deviation, audit observation, or CAPA description
        </label>
        <textarea
          id="similarity-query"
          ref={textareaRef}
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          rows={6}
          maxLength={MAX_CHARS + 100}
          placeholder="e.g. Deviation in sterility assurance — contamination found during aseptic fill. Root cause: inadequate gowning procedure…"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-sentinel-500 resize-y"
        />
        <div className="flex items-center justify-between mt-1">
          <span className={`text-xs ${overLimit ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
            {charCount} / {MAX_CHARS.toLocaleString()} characters
          </span>
          <button
            type="submit"
            disabled={loading || overLimit || queryText.trim().length === 0 || !session}
            className="px-4 py-2 text-sm font-medium text-white bg-sentinel-700 rounded hover:bg-sentinel-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Searching…' : 'Find Similar'}
          </button>
        </div>
        {overLimit && (
          <p className="text-xs text-red-600 mt-1">
            Text exceeds {MAX_CHARS.toLocaleString()} character limit. Please shorten the query.
          </p>
        )}
      </form>

      {requestError && (
        <div className="mb-6">
          <ApiErrorNotice error={requestError} fallbackMessage="Similarity search failed." />
        </div>
      )}

      {result && (
        <>
          {/* Coverage gap banner */}
          {(corpusIncomplete || coverageGaps.length > 0) && (
            <div
              className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-6"
              data-testid="coverage-gap-banner"
            >
              <p className="text-sm font-semibold text-amber-800 mb-2">Coverage gaps detected</p>
              <ul className="space-y-1">
                {coverageGaps.map((gap, i) => (
                  <li key={i} className="text-sm text-amber-700">
                    <span className="font-mono text-xs mr-1">[{gap.code}]</span>
                    {gap.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Results */}
          {results.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-slate-400 text-sm">
              No similar findings found.{' '}
              {corpusIncomplete
                ? 'The embedding corpus is not yet populated.'
                : 'Try a different query.'}
            </div>
          ) : (
            <div data-testid="similarity-results">
              <p className="text-sm text-slate-500 mb-4">
                {results.length} similar finding{results.length === 1 ? '' : 's'} found.
              </p>
              <ul className="space-y-4">
                {results.map((r, i) => (
                  <li key={i} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <p className="text-xs text-slate-500 mb-0.5">
                          {r.document.agency ?? '—'} ·{' '}
                          {r.document.document_type ?? '—'} ·{' '}
                          {r.document.issue_date ?? r.document.posted_date ?? '—'}
                        </p>
                        <p className="text-sm font-medium text-slate-800">
                          {r.document.title ?? r.document.canonical_url ?? r.source_document_id}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {confidenceBadge(r.confidence)}
                        <span className="text-xs text-slate-400 tabular-nums">
                          {(r.similarity * 100).toFixed(1)}% match
                        </span>
                      </div>
                    </div>
                    {r.finding?.finding_text && (
                      <p className="text-sm text-slate-700 mb-2">{r.finding.finding_text}</p>
                    )}
                    {r.excerpt && (
                      <blockquote className="border-l-2 border-blue-200 pl-3 text-xs text-slate-500 italic">
                        {r.excerpt}
                      </blockquote>
                    )}
                    {r.document.canonical_url && (
                      <a
                        href={r.document.canonical_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-sentinel-700 hover:underline mt-2 block"
                      >
                        View source →
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
